import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiFetch } from '@shared/api/client';
import { ApiError } from '@shared/api/problem';
import { queryKeys } from '@shared/api/queryKeys';

/** Роль одна: админки и ролей администратора в MVP нет (CLAUDE.md §2). */
export type UserRole = 'customer';

/** Ответ `GET /api/v1/auth/me` и `POST /api/v1/auth/code/verify`. */
export interface AuthUser {
  id: string;
  email: string | null;
  display_name: string | null;
  role: UserRole;
  locale: string;
}

export interface AuthSessionInfo {
  type: 'user';
  expires_at: string;
}

export interface AuthSessionResponse {
  user: AuthUser;
  session: AuthSessionInfo;
}

export const ME_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Текущий пользователь или `null`.
 *
 * 401 — это не сбой, а штатный ответ «сессии нет»: гость видит публичные
 * страницы без единой ошибки в консоли. Остальные коды остаются ошибками,
 * иначе недоступный backend выглядел бы как выход из аккаунта.
 */
export function useMe(): UseQueryResult<AuthSessionResponse | null> {
  return useQuery({
    queryKey: queryKeys.me(),
    queryFn: async () => {
      try {
        return await apiFetch<AuthSessionResponse>('/auth/me');
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: ME_STALE_TIME_MS,
    retry: false,
  });
}

/** Выход. Backend отзывает сессию и очищает cookie, клиент — весь кеш. */
export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiFetch<undefined>('/auth/logout', { method: 'POST' }),
    onSuccess: async () => {
      queryClient.setQueryData(queryKeys.me(), null);
      // Персональные данные (избранное, сравнение, подбор) не должны пережить
      // выход даже в кеше вкладки.
      await queryClient.invalidateQueries();
    },
  });
}
